<template>
  <!-- 单独占位，置顶栏不遮挡官网原有导航；整个组件只由 App 渲染一次。 -->
  <div class="website-top-login-placeholder" :style="{ height: barHeight + 'px' }">
    <div ref="topBar" class="website-top-login-bar">
      <div class="top-login-inner">
        <div class="top-login-welcome">
          <span class="top-login-greeting" :title="companyName || null">欢迎您<span v-if="companyName">，{{ companyName }}</span>！</span>
          <span class="top-login-date">
            <i class="el-icon-date" aria-hidden="true"></i>
            {{ dateText }}
            <span class="top-login-weekday">{{ weekdayText }}</span>
          </span>
        </div>
        <div v-if="user" class="top-login-actions">
          <router-link class="top-login-link top-login-link-primary" to="/service/homePage">进入服务平台</router-link>
        </div>
        <form v-else class="top-login-form" aria-label="企业账号登录" :aria-busy="submitting" :aria-describedby="errorMessage ? 'website-top-login-error' : null" @submit.prevent="submitCompanyLogin">
          <div class="top-login-field">
            <label for="website-company-name">企业名称</label>
            <el-input
              id="website-company-name"
              ref="companyInput"
              v-model="form.companyName"
              class="top-company-input"
              name="companyName"
              placeholder="请输入企业名称"
              autocomplete="username"
              size="small"
              :disabled="submitting"
              @input="clearError"
            />
          </div>
          <div class="top-login-field">
            <label for="website-company-password">密码</label>
            <el-input
              id="website-company-password"
              ref="passwordInput"
              v-model="form.password"
              class="top-password-input"
              name="password"
              type="password"
              placeholder="请输入密码"
              autocomplete="current-password"
              size="small"
              :disabled="submitting"
              @input="clearError"
            />
          </div>
          <div class="top-login-form-actions">
            <el-button class="top-login-button" type="primary" size="small" native-type="submit" :loading="submitting">
              登录
            </el-button>
            <router-link class="top-login-link top-login-link-primary" :to="companyRegisterRoute">注册</router-link>
          </div>
          <p v-if="errorMessage" id="website-top-login-error" class="top-login-error" role="alert">{{ errorMessage }}</p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { pwdLoginByCompanyName } from '@/api/register'
import Cache from '@/utils/cache'
import { saveLoginSession } from '@/utils/authSession'

export default {
  name: 'WebsiteTopLoginBar',
  data() {
    return {
      form: { companyName: '', password: '' },
      submitting: false,
      errorMessage: '',
      currentTime: Date.now(),
      barHeight: 45,
      clockInterval: null,
      resizeObserver: null,
      active: true,
      companyRegisterRoute: {
        path: '/public/service/login',
        query: { mode: 'register', accountType: 'company' }
      }
    }
  },
  computed: {
    ...mapGetters(['user']),
    companyName() {
      return this.user?.companyName || this.user?.name || ''
    },
    dateText() {
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai', year: 'numeric', month: 'numeric', day: 'numeric'
      }).formatToParts(this.currentTime)
      const date = {}
      parts.forEach(part => { date[part.type] = part.value })
      return `${date.year}年${date.month}月${date.day}日`
    },
    weekdayText() {
      return new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai', weekday: 'long'
      }).format(this.currentTime)
    }
  },
  mounted() {
    this.updateBarHeight()
    window.addEventListener('resize', this.updateBarHeight)
    document.addEventListener('visibilitychange', this.updateClock)
    this.clockInterval = setInterval(this.updateClock, 60000)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateBarHeight)
      this.resizeObserver.observe(this.$refs.topBar)
    }
  },
  updated() {
    this.updateBarHeight()
  },
  beforeDestroy() {
    this.active = false
    this.form.password = ''
    clearInterval(this.clockInterval)
    window.removeEventListener('resize', this.updateBarHeight)
    document.removeEventListener('visibilitychange', this.updateClock)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    updateClock() {
      this.currentTime = Date.now()
    },
    updateBarHeight() {
      if (this.$refs.topBar) this.barHeight = this.$refs.topBar.offsetHeight
    },
    clearError() {
      this.errorMessage = ''
    },
    async submitCompanyLogin() {
      if (this.submitting) return
      const companyName = this.form.companyName.trim()
      if (!companyName || !this.form.password) {
        this.errorMessage = companyName ? '请输入密码' : '请输入企业名称'
        this.$refs[companyName ? 'passwordInput' : 'companyInput'].focus()
        return
      }
      this.submitting = true
      this.errorMessage = ''
      try {
        const response = await pwdLoginByCompanyName({
          uuid: Cache.get('uuid'), companyName, password: this.form.password
        })
        if (!this.active) return
        if (response?.resultCode !== '200') {
          this.errorMessage = response?.resultMsg || '登录未成功，请检查企业名称和密码'
          return
        }
        saveLoginSession(this.$store, response.resultData)
        this.form.password = ''
        // 官网快捷登录始终进入平台首页，不使用之前遗留的目标页面。
        localStorage.removeItem('redirectUrl')
        this.$message.success('登录成功')
        await this.$router.replace('/service/homePage')
      } catch (error) {
        if (this.active && !['NavigationDuplicated', 'NavigationCancelled', 'NavigationRedirected'].includes(error?.name)) {
          if (this.user) {
            this.$message.error('页面跳转失败，请点击进入服务平台')
          } else {
            this.errorMessage = '登录失败，请稍后重试'
          }
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.website-top-login-placeholder {
  --top-login-accent: var(--theme-GLOBAL_COLOR, #165DFF);
  --top-login-control-height: 32px;
}
.website-top-login-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  background: #f7f8fa;
  border-bottom: 1px solid #e5e6eb;
  color: #4e5969;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: 13px;
  box-sizing: border-box;
}
.top-login-inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 20px;
  width: 100%;
  min-height: 44px;
  margin: 0 auto;
  padding: 6px var(--website-header-gutter, 100px);
  box-sizing: border-box;
}
.top-login-welcome,
.top-login-actions,
.top-login-field,
.top-login-form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.top-login-welcome {
  min-width: 0;
  min-height: var(--top-login-control-height);
  flex-wrap: wrap;
  gap: 6px 16px;
  line-height: 20px;
}
.top-login-greeting {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.top-login-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.top-login-date > i {
  color: #86909c;
  font-size: 14px;
}
.top-login-weekday {
  margin-left: 2px;
}
.top-login-form {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 6px 12px;
  min-width: 0;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: auto;
  padding-left: 20px;
  border-left: 1px solid #e5e6eb;
}
.top-login-field {
  min-width: 0;
}
.top-login-form label {
  display: inline-flex;
  align-items: center;
  min-height: var(--top-login-control-height);
  white-space: nowrap;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}
.top-company-input {
  width: 180px;
}
.top-password-input {
  width: 144px;
}
.top-login-field .el-input {
  display: inline-flex;
  align-items: center;
  height: var(--top-login-control-height);
}
.top-login-field ::v-deep .el-input__inner {
  height: var(--top-login-control-height);
  padding: 0 10px;
  border-color: #dce0e6;
  border-radius: 6px;
  background: #fff;
  color: #303133;
  font-family: inherit;
  font-size: 13px;
  line-height: normal;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.top-login-field ::v-deep .el-input__inner:hover {
  border-color: #a8b0bc;
}
.top-login-field ::v-deep .el-input__inner:focus {
  border-color: var(--top-login-accent);
  box-shadow: 0 0 0 2px #e5e6eb;
}
.top-login-field ::v-deep .el-input.is-disabled .el-input__inner {
  border-color: #e5e6eb;
  background: #f2f3f5;
  color: #86909c;
}
.top-login-button,
.top-login-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: var(--top-login-control-height);
  margin: 0;
  padding: 0 14px;
  border: 1px solid var(--top-login-accent);
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.top-login-button,
.top-login-link-primary {
  background: var(--top-login-accent);
  color: #fff;
}
.top-login-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}
.top-login-link:not(.top-login-link-primary) {
  background: #fff;
  color: var(--top-login-accent);
}
.top-login-button:hover,
.top-login-button:focus,
.top-login-button:active {
  background: var(--top-login-accent);
  border-color: var(--top-login-accent);
  color: #fff;
}
.top-login-button:hover,
.top-login-link:hover {
  border-color: var(--top-login-accent);
  box-shadow: inset 0 0 0 40px rgba(0, 0, 0, 0.04);
}
.top-login-button:active,
.top-login-link:active {
  box-shadow: inset 0 0 0 40px rgba(0, 0, 0, 0.1);
}
.top-login-button:focus-visible,
.top-login-link:focus-visible {
  outline: 2px solid var(--top-login-accent);
  outline-offset: 3px;
}
.top-login-error {
  grid-column: 1 / -1;
  margin: 0;
  color: #b42318;
  line-height: 20px;
}
@media (max-width: 960px) {
  .top-login-inner {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .top-login-form {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    width: 100%;
    padding-left: 0;
    border-left: 0;
  }
  .top-login-field .el-input {
    width: 100%;
    min-width: 0;
  }
}
@media (max-width: 640px) {
  .website-top-login-placeholder {
    --top-login-control-height: 36px;
  }
  .top-login-inner {
    gap: 10px;
  }
  .top-login-greeting {
    max-width: 100%;
  }
  .top-login-form {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px 12px;
  }
  .top-login-field {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
  .top-login-form label {
    min-height: 20px;
  }
  .top-login-form-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}
@media (prefers-reduced-motion: reduce) {
  .top-login-field ::v-deep .el-input__inner,
  .top-login-button,
  .top-login-link {
    transition: none;
  }
}
</style>
